/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Group,
  MultiSelect,
  Select,
  Textarea,
  TextInput,
  Loader,
  Center,
  NumberInput,
  Text,
  Checkbox,
  Switch,
  Paper,
  ActionIcon,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { z } from "zod";

import { User } from "types";
import { useAddDepartment } from "api/department/addDepartment";
import { useGetOrg } from "api/organizations/getOrg";
import { useUser } from "hooks/useUser";
import { useGetOrganizations } from "api/superAdmin/organizationManagement";
import { useUploadFile } from "api/upload/uploadFile";
import { useGetDirectors } from "api/users/getdirector";
import { useGetUserPermissions } from "api/permission/getpermission";
import { CANADA_TIMEZONES, DEFAULT_ORG_TIMEZONE } from "utils/timezoneDate";

type Organization = {
  id: string;
  name: string;
};

type AddDepartmentFormProps = {
  onClose: () => void;
  user: User;
  organizations?: Organization[];
  users?: User[];
};

interface RecurringConfig {
  id: string;
  recurrenceType: "day" | "weekly" | "biweekly" | "monthly";
  interval: number;
  selectedDays: string[];
  selectedDate?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
}

type FormValues = {
  name: string;
  description?: string;
  handbook?: string;
  status: "active" | "inactive";
  organization: string;
  members: string[];
  supervisor?: string | null;
  director?: string | null;
  sameAsOrg: boolean;

  // Working hours
  startTime?: string | null;
  endTime?: string | null;
  totalHours?: number | undefined | null;

  // Break config
  breakTimeEnabled?: boolean;
  hours?: number | null;
  breakStart?: string | null;
  breakEnd?: string | null;
  breakType?: "duration" | "timeRange";
  breakDurationHours?: number;

  // Overtime config - COMPLETE
  overtimeAllowed?: boolean;
  maxHoursPerDay?: number | null;
  maxHoursPerMonth?: number | null;
  overtimeRate?: number | null;
  overtimeRequiresApproval?: boolean;

  // Late arrival config - COMPLETE
  graceMinutes?: number | null;
  lateMarkHalfDayAfterMinutes?: number | null;
  lateRequiresApproval?: boolean;
  lateDeductionEnabled?: boolean;
  lateDeductMinutesFor?: number | null;
  lateForEveryMinutes?: number | null;

  // Early departure config - NEW
  earlyGraceMinutes?: number | null;
  earlyMarkHalfDayBeforeMinutes?: number | null;

  // Location config - NEW
  isLocationCheck?: boolean;
  lng?: string | null;
  lat?: string | null;

  // Recurring config - NEW
  enableRecurringConfig?: boolean;
  recurringConfigs: RecurringConfig[];
  recurringStartDate?: string;

  // Logo
  departmentLogo?: string | null;

  // Timezone
  timezone?: string;
};

const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

const createDepartmentSchema = z
  .object({
    name: z.string().trim().min(1, { message: "Please enter department name" }),
    description: z.string().trim().optional(),
    handbook: z.string().trim().optional().nullable(),
    status: z.enum(["active", "inactive"]),
    organization: z
      .string()
      .min(1, { message: "Please select an organization" }),
    members: z.preprocess((val) => {
      if (val === undefined || val === null) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === "string") return [val];
      return [];
    }, z.array(z.string())),
    supervisor: z.string().optional().nullable(),
    director: z.string().optional().nullable(),
    sameAsOrg: z.boolean(),
    startTime: z.string().optional().nullable(),
    endTime: z.string().optional().nullable(),
    hours: z.number().optional().nullable(),
    breakTimeEnabled: z.boolean().optional(),
    breakStart: z.string().optional().nullable(),
    breakEnd: z.string().optional().nullable(),
    breakType: z.enum(["duration", "timeRange"]).optional(),
    breakDurationHours: z.number().optional(),
    overtimeAllowed: z.boolean().optional(),
    maxHoursPerDay: z.number().optional().nullable(),
    maxHoursPerMonth: z.number().optional().nullable(),
    overtimeRate: z.number().optional().nullable(),
    overtimeRequiresApproval: z.boolean().optional(),
    graceMinutes: z.number().optional().nullable(),
    lateMarkHalfDayAfterMinutes: z.number().optional().nullable(),
    lateRequiresApproval: z.boolean().optional(),
    lateDeductionEnabled: z.boolean().optional(),
    lateDeductMinutesFor: z.number().optional().nullable(),
    lateForEveryMinutes: z.number().optional().nullable(),
    earlyGraceMinutes: z.number().optional().nullable(),
    earlyMarkHalfDayBeforeMinutes: z.number().optional().nullable(),
    isLocationCheck: z.boolean().optional(),
    lng: z.string().optional().nullable(),
    lat: z.string().optional().nullable(),
    enableRecurringConfig: z.boolean().optional(),
    recurringConfigs: z.array(z.any()).optional(),
    recurringStartDate: z.string().optional(),
    totalHours: z.number().optional().nullable(),
    departmentLogo: z.string().optional().nullable(),
    timezone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const start = data.startTime;
    const end = data.endTime;

    if ((start && !end) || (!start && end)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Both startTime and endTime must be provided together",
        path: start ? ["endTime"] : ["startTime"],
      });
      return;
    }

    if (start && end) {
      if (!timeRegex.test(start)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "startTime must be in HH:mm format",
          path: ["startTime"],
        });
      }
      if (!timeRegex.test(end)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "endTime must be in HH:mm format",
          path: ["endTime"],
        });
      }
    }
  });

export default function AddDepartmentForm({
  onClose,
  organizations = [],
  users = [],
}: AddDepartmentFormProps) {
  const createDepartmentMutation = useAddDepartment();
  const uploadFileMutation = useUploadFile();
  const [page] = React.useState(1);
  const limit = 20;
  const { data: orgsResponse, isLoading: orgsLoading } = useGetOrganizations();

  const { user } = useUser();
  const userId = user?._id ?? "";
  const { data: permResp } = useGetUserPermissions(userId);
  const permissions = permResp?.data?.permissions ?? {};
  const supervisor = user?.reportingManager;

  const { data: org } = useGetOrg(user?.org?._id);

  // Directors fetch
  const { data: directorsData, isLoading: directorsLoading } = useGetDirectors(
    user?.org?._id
  );

  const [handbookFile, setHandbookFile] = React.useState<File | null>(null);
  const [handbookPreview, setHandbookPreview] = React.useState<string>("");
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [logoPreview, setLogoPreview] = React.useState<string>("");
  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      description: "",
      handbook: "",
      status: "active",
      organization: "",
      members: [],
      supervisor: "",
      director: "",
      sameAsOrg: true,
      startTime: undefined,
      endTime: undefined,
      hours: undefined,
      totalHours: undefined,
      breakTimeEnabled: false,
      breakStart: undefined,
      breakEnd: undefined,
      breakType: "duration",
      breakDurationHours: 1,
      overtimeAllowed: false,
      maxHoursPerDay: undefined,
      maxHoursPerMonth: undefined,
      overtimeRate: undefined,
      overtimeRequiresApproval: false,
      graceMinutes: undefined,
      lateMarkHalfDayAfterMinutes: undefined,
      lateRequiresApproval: false,
      lateDeductionEnabled: false,
      lateDeductMinutesFor: undefined,
      lateForEveryMinutes: undefined,
      earlyGraceMinutes: undefined,
      earlyMarkHalfDayBeforeMinutes: undefined,
      isLocationCheck: false,
      lng: undefined,
      lat: undefined,
      enableRecurringConfig: false,
      recurringConfigs: [],
      recurringStartDate: undefined,
      departmentLogo: null,
      timezone: DEFAULT_ORG_TIMEZONE,
    },
    validate: zodResolver(createDepartmentSchema),
  });

  const dummyOrgs: Organization[] = [
    { id: "org_1", name: "Default Organization" },
    { id: "org_2", name: "Software" },
    { id: "org_3", name: "Finance" },
  ];

  const apiOrgOptions = useMemo(() => {
    if (
      orgsResponse?.organizations &&
      Array.isArray(orgsResponse.organizations)
    ) {
      return orgsResponse.organizations.map((o: any) => ({
        value: String(o.id ?? o._id ?? o.id ?? ""),
        label: o.name,
      }));
    }
    return [];
  }, [orgsResponse]);

  const orgList = useMemo(
    () =>
      apiOrgOptions.length > 0
        ? apiOrgOptions
        : organizations.length > 0
        ? organizations.map((o) => ({ value: o.id, label: o.name }))
        : dummyOrgs.map((o) => ({ value: o.id, label: o.name })),
    [apiOrgOptions, organizations]
  );

  const userOrgName = user?.org?.name ?? "";
  const userOrgId = user?.org?._id ?? user?.org?._id ?? "";

  const selectedOrgOption =
    orgList.find(
      (o: any) =>
        String(o.label).trim() === String(userOrgName).trim() ||
        String(o.value) === String(userOrgId)
    ) ?? null;

  const orgListFiltered = selectedOrgOption ? [selectedOrgOption] : [];
  console.log("directorsData", directorsData);
  // build director options
  const directorOptions = useMemo(() => {
    if (!directorsData?.data || !Array.isArray(directorsData.data)) return [];
    return directorsData.data.map((d: any) => ({
      value: d._id,
      label:
        `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim() || d.email || d._id,
    }));
  }, [directorsData]);

  const [localUserOptionsw, setLocalUserOptionsw] = useState<
    { value: string; label: string }[]
  >([]);

  // Add supervisor to dropdown options if available (but don't auto-select)
  useEffect(() => {
    if (supervisor) {
      setLocalUserOptionsw((prev) => {
        const exists = prev.some((u) => u.value === supervisor._id);
        if (!exists) {
          return [
            ...prev,
            {
              value: supervisor._id,
              label: `${supervisor.firstName} ${supervisor.lastName || ""}`,
            },
          ];
        }
        return prev;
      });
    }
  }, [supervisor]);
  useEffect(() => {
    if (
      selectedOrgOption &&
      form.values.organization !== selectedOrgOption.value
    ) {
      form.setFieldValue("organization", selectedOrgOption.value);
    }
  }, [selectedOrgOption]);

  const membersFromOrg = useMemo(() => {
    const map: Record<string, any[]> = {};
    if (
      orgsResponse?.organizations &&
      Array.isArray(orgsResponse.organizations)
    ) {
      for (const o of orgsResponse.organizations) {
        const key = String(o.id ?? o.id ?? o.id ?? "");
        map[key] = Array.isArray(o.members) ? o.members : [];
      }
    }
    return map;
  }, [orgsResponse]);

  const fallbackMembers = useMemo(() => {
    const base = org?.members ?? users ?? [];
    return base.map((u: any) => ({
      value: String(u._id ?? u.id ?? ""),
      label: u.firstName
        ? `${u.firstName} ${u.lastName ?? ""}`
        : u.name ?? u.email ?? "Unknown",
    }));
  }, [org, users]);

  const shallowEqualArray = (a: any[] | undefined, b: any[] | undefined) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i].value !== b[i].value || a[i].label !== b[i].label) return false;
    }
    return true;
  };

  const [localUserOptions, setLocalUserOptions] = useState<
    { value: string; label: string }[]
  >(() => fallbackMembers);

  const buildDedupe = (arrs: Array<{ value: string; label: string }[]>) => {
    const deduped: { value: string; label: string }[] = [];
    for (const arr of arrs) {
      for (const o of arr) {
        if (!deduped.find((d) => d.value === o.value)) deduped.push(o);
      }
    }
    return deduped;
  };

  const buildOptionsForOrg = (orgId?: string | null) => {
    const orgMembers = (orgId ? membersFromOrg[String(orgId)] : []) ?? [];
    const orgMemberOptions = orgMembers.map((m: any) => ({
      value: String(m._id ?? m.id ?? ""),
      label:
        m.firstName || m.email
          ? `${m.firstName ? `${m.firstName} ${m.lastName ?? ""}` : m.email}`
          : String(m._id ?? m.id ?? ""),
    }));

    return buildDedupe([orgMemberOptions, fallbackMembers]);
  };

  const getOrgById = (orgId?: string | null) => {
    if (!orgId) return null;
    if (
      orgsResponse?.organizations &&
      Array.isArray(orgsResponse.organizations)
    ) {
      return orgsResponse.organizations.find(
        (o: any) => String(o.id ?? o._id ?? "") === String(orgId)
      );
    }
    if (organizations && organizations.length > 0) {
      return organizations.find((o: any) => String(o.id) === String(orgId));
    }
    return null;
  };
  //for test comment

  const extractOrgWorkingConfig = (orgItem: any) => {
    if (!orgItem) return null;

    // Handle array format for workingHours
    let wh: any = {};
    if (
      Array.isArray(orgItem.workingHours) &&
      orgItem.workingHours.length > 0
    ) {
      wh = orgItem.workingHours[0]; // Get first config
    } else if (
      orgItem.workingHours &&
      typeof orgItem.workingHours === "object"
    ) {
      wh = orgItem.workingHours;
    }

    const br = orgItem.break ?? wh.break ?? {};
    const ot = orgItem.overtimeConfig ?? {};
    const late = orgItem.lateArrivalConfig ?? {};
    const early = orgItem.earlyDepartureConfig ?? {};

    return {
      startTime: wh.startTime ?? orgItem.startTime ?? null,
      endTime: wh.endTime ?? orgItem.endTime ?? null,
      breakTimeEnabled:
        br && Object.keys(br).length > 0 && br.enabled !== false,
      breakType: (br.type ?? "duration") as "duration" | "timeRange",
      breakDurationHours:
        typeof br.durationHours === "number"
          ? br.durationHours
          : typeof br.hours === "number"
          ? br.hours
          : typeof wh.hours === "number"
          ? wh.hours
          : orgItem.hours ?? null,
      breakStart: br.startTime ?? wh.breakStart ?? orgItem.breakStart ?? null,
      breakEnd: br.endTime ?? wh.breakEnd ?? orgItem.breakEnd ?? null,

      // Overtime config - COMPLETE
      overtimeAllowed:
        typeof ot.allowed === "boolean"
          ? ot.allowed
          : typeof wh.overtimeAllowed === "boolean"
          ? wh.overtimeAllowed
          : typeof orgItem.overtimeAllowed === "boolean"
          ? orgItem.overtimeAllowed
          : false,
      maxHoursPerDay:
        typeof ot.maxHoursPerDay === "number"
          ? ot.maxHoursPerDay
          : typeof wh.maxHoursPerDay === "number"
          ? wh.maxHoursPerDay
          : orgItem.maxHoursPerDay ?? null,
      maxHoursPerMonth:
        typeof ot.maxHoursPerMonth === "number"
          ? ot.maxHoursPerMonth
          : orgItem.maxHoursPerMonth ?? null,
      overtimeRate:
        typeof ot.overtimeRate === "number"
          ? ot.overtimeRate
          : orgItem.overtimeRate ?? null,
      overtimeRequiresApproval:
        typeof ot.requiresApproval === "boolean" ? ot.requiresApproval : false,

      // Late arrival config - COMPLETE
      graceMinutes:
        typeof late.graceMinutes === "number"
          ? late.graceMinutes
          : typeof wh.graceMinutes === "number"
          ? wh.graceMinutes
          : orgItem.graceMinutes ?? null,
      lateMarkHalfDayAfterMinutes:
        typeof late.markAsHalfDayAfter === "number"
          ? late.markAsHalfDayAfter
          : typeof late.markHalfDayAfterMinutes === "number"
          ? late.markHalfDayAfterMinutes
          : null,
      lateDeductionEnabled:
        typeof late.deductionEnabled === "boolean"
          ? late.deductionEnabled
          : false,
      lateDeductMinutesFor:
        typeof late.deductMinutesFor === "number"
          ? late.deductMinutesFor
          : null,
      lateForEveryMinutes:
        typeof late.forEveryMinutes === "number" ? late.forEveryMinutes : null,

      // Early departure config - NEW
      earlyGraceMinutes:
        typeof early.graceMinutes === "number" ? early.graceMinutes : null,
      earlyMarkHalfDayBeforeMinutes:
        typeof early.markHalfDayBeforeMinutes === "number"
          ? early.markHalfDayBeforeMinutes
          : null,

      // Location config - NEW
      isLocationCheck:
        typeof orgItem.isLocationCheck === "boolean"
          ? orgItem.isLocationCheck
          : false,
      lng: orgItem.location?.lng ?? null,
      lat: orgItem.location?.lat ?? null,

      totalHours:
        typeof wh.totalHours === "number"
          ? wh.totalHours
          : orgItem.totalHours ?? null,
    };
  };

  const applyOrgConfigToForm = (orgId?: string | null) => {
    const orgItem = getOrgById(orgId) ?? org ?? null;
    const cfg = extractOrgWorkingConfig(orgItem);
    if (!cfg) return;

    // Working hours
    if (form.values.startTime !== (cfg.startTime ?? undefined)) {
      form.setFieldValue("startTime", cfg.startTime ?? undefined);
    }
    if (form.values.endTime !== (cfg.endTime ?? undefined)) {
      form.setFieldValue("endTime", cfg.endTime ?? undefined);
    }
    if (form.values.totalHours !== (cfg.totalHours ?? undefined)) {
      form.setFieldValue("totalHours", cfg.totalHours ?? undefined);
    }

    // Break config
    if (form.values.breakType !== (cfg.breakType ?? "duration")) {
      form.setFieldValue("breakType", cfg.breakType ?? "duration");
    }
    if (
      form.values.breakDurationHours !== (cfg.breakDurationHours ?? undefined)
    ) {
      form.setFieldValue(
        "breakDurationHours",
        cfg.breakDurationHours ?? undefined
      );
    }
    if (form.values.breakStart !== (cfg.breakStart ?? undefined)) {
      form.setFieldValue("breakStart", cfg.breakStart ?? undefined);
    }
    if (form.values.breakEnd !== (cfg.breakEnd ?? undefined)) {
      form.setFieldValue("breakEnd", cfg.breakEnd ?? undefined);
    }

    // Overtime config - COMPLETE
    if (form.values.overtimeAllowed !== !!cfg.overtimeAllowed) {
      form.setFieldValue("overtimeAllowed", !!cfg.overtimeAllowed);
    }
    if (form.values.maxHoursPerDay !== (cfg.maxHoursPerDay ?? undefined)) {
      form.setFieldValue("maxHoursPerDay", cfg.maxHoursPerDay ?? undefined);
    }
    if (form.values.maxHoursPerMonth !== (cfg.maxHoursPerMonth ?? undefined)) {
      form.setFieldValue("maxHoursPerMonth", cfg.maxHoursPerMonth ?? undefined);
    }
    if (form.values.overtimeRate !== (cfg.overtimeRate ?? undefined)) {
      form.setFieldValue("overtimeRate", cfg.overtimeRate ?? undefined);
    }
    if (
      form.values.overtimeRequiresApproval !== !!cfg.overtimeRequiresApproval
    ) {
      form.setFieldValue(
        "overtimeRequiresApproval",
        !!cfg.overtimeRequiresApproval
      );
    }

    // Late arrival config - COMPLETE
    if (form.values.graceMinutes !== (cfg.graceMinutes ?? undefined)) {
      form.setFieldValue("graceMinutes", cfg.graceMinutes ?? undefined);
    }
    if (
      form.values.lateMarkHalfDayAfterMinutes !==
      (cfg.lateMarkHalfDayAfterMinutes ?? undefined)
    ) {
      form.setFieldValue(
        "lateMarkHalfDayAfterMinutes",
        cfg.lateMarkHalfDayAfterMinutes ?? undefined
      );
    }
    if (form.values.lateDeductionEnabled !== !!cfg.lateDeductionEnabled) {
      form.setFieldValue("lateDeductionEnabled", !!cfg.lateDeductionEnabled);
    }
    if (
      form.values.lateDeductMinutesFor !==
      (cfg.lateDeductMinutesFor ?? undefined)
    ) {
      form.setFieldValue(
        "lateDeductMinutesFor",
        cfg.lateDeductMinutesFor ?? undefined
      );
    }
    if (
      form.values.lateForEveryMinutes !== (cfg.lateForEveryMinutes ?? undefined)
    ) {
      form.setFieldValue(
        "lateForEveryMinutes",
        cfg.lateForEveryMinutes ?? undefined
      );
    }

    // Early departure config - NEW
    if (
      form.values.earlyGraceMinutes !== (cfg.earlyGraceMinutes ?? undefined)
    ) {
      form.setFieldValue(
        "earlyGraceMinutes",
        cfg.earlyGraceMinutes ?? undefined
      );
    }
    if (
      form.values.earlyMarkHalfDayBeforeMinutes !==
      (cfg.earlyMarkHalfDayBeforeMinutes ?? undefined)
    ) {
      form.setFieldValue(
        "earlyMarkHalfDayBeforeMinutes",
        cfg.earlyMarkHalfDayBeforeMinutes ?? undefined
      );
    }

    // Location config - NEW
    if (form.values.isLocationCheck !== !!cfg.isLocationCheck) {
      form.setFieldValue("isLocationCheck", !!cfg.isLocationCheck);
    }
    if (form.values.lng !== (cfg.lng ?? undefined)) {
      form.setFieldValue("lng", cfg.lng ?? undefined);
    }
    if (form.values.lat !== (cfg.lat ?? undefined)) {
      form.setFieldValue("lat", cfg.lat ?? undefined);
    }
  };

  const handleOrgChange = (selectedOrgId: string | null) => {
    const opts = buildOptionsForOrg(selectedOrgId ?? "");
    if (!shallowEqualArray(opts, localUserOptions)) {
      setLocalUserOptions(opts);
    }

    const orgMemberIds = new Set(opts.map((o) => o.value));
    const filteredMembers = (form.values.members ?? []).filter((id) =>
      orgMemberIds.has(id)
    );
    if (
      filteredMembers.length !== form.values.members.length ||
      !filteredMembers.every((m, i) => m === form.values.members[i])
    ) {
      form.setFieldValue("members", filteredMembers);
    }

    const currentSupervisor = form.values.supervisor ?? "";
    if (currentSupervisor && !orgMemberIds.has(currentSupervisor)) {
      form.setFieldValue("supervisor", "");
    }

    if (form.values.sameAsOrg) {
      applyOrgConfigToForm(selectedOrgId);
    }
  };

  const orgInput = form.getInputProps("organization");
  const handleOrgSelectChange = (val: string | null) => {
    orgInput.onChange(val);
    handleOrgChange(val);
  };

  const computeTotalHours = (start?: string | null, end?: string | null) => {
    if (!start || !end) return undefined;
    if (!timeRegex.test(start) || !timeRegex.test(end)) return undefined;

    const [sh, sm] = start.split(":").map((s) => parseInt(s, 10));
    const [eh, em] = end.split(":").map((s) => parseInt(s, 10));

    let startMinutes = sh * 60 + sm;
    let endMinutes = eh * 60 + em;

    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }

    const diffMinutes = endMinutes - startMinutes;
    const hours = diffMinutes / 60;
    return Math.round(hours * 100) / 100;
  };

  useEffect(() => {
    const s = form.values.startTime as string | undefined | null;
    const e = form.values.endTime as string | undefined | null;
    const computed = computeTotalHours(s ?? undefined, e ?? undefined);
    if (computed !== form.values.totalHours) {
      form.setFieldValue("totalHours", computed);
    }
  }, [form.values.startTime, form.values.endTime]);

  // Auto-calculate total hours for each recurring configuration entry
  useEffect(() => {
    if (form.values.enableRecurringConfig && form.values.recurringConfigs.length > 0) {
      const updatedConfigs = form.values.recurringConfigs.map((config) => {
        const computed = computeTotalHours(config.startTime, config.endTime);
        if (computed !== undefined && computed !== config.totalHours) {
          return { ...config, totalHours: computed };
        }
        return config;
      });
      if (JSON.stringify(updatedConfigs) !== JSON.stringify(form.values.recurringConfigs)) {
        form.setFieldValue("recurringConfigs", updatedConfigs);
      }
    }
  }, [
    form.values.recurringConfigs.map((c) => c.startTime).join(","),
    form.values.recurringConfigs.map((c) => c.endTime).join(","),
    form.values.enableRecurringConfig,
  ]);

  useEffect(() => {
    const opts = form.values.organization
      ? buildOptionsForOrg(form.values.organization)
      : fallbackMembers;
    if (!shallowEqualArray(opts, localUserOptions)) {
      setLocalUserOptions(opts);
    }
  }, [orgsResponse, org, users, form.values.organization]);

  useEffect(() => {
    if (form.values.sameAsOrg) {
      applyOrgConfigToForm(form.values.organization ?? undefined);
    }
  }, [form.values.sameAsOrg]);

  const addRecurringConfig = () => {
    form.insertListItem("recurringConfigs", {
      id: `recurring-${Date.now()}-${Math.random()}`,
      recurrenceType: "weekly",
      interval: 1,
      selectedDays: [],
      selectedDate: "",
      startTime: "08:00",
      endTime: "16:00",
      totalHours: 8,
    });
  };

  const removeRecurringConfig = (index: number) => {
    form.removeListItem("recurringConfigs", index);
  };

  // Auto-add the first recurring config entry when the checkbox is toggled on
  useEffect(() => {
    if (
      form.values.enableRecurringConfig &&
      form.values.recurringConfigs.length === 0
    ) {
      addRecurringConfig();
    }
  }, [form.values.enableRecurringConfig]);

  const handleMembersChange = (val: string[]) => {
    form.setFieldValue("members", val ?? []);
  };

  const handleSubmit = async (values: FormValues) => {
    const normalizedMembers = Array.isArray(values.members)
      ? values.members
      : values.members
      ? [String(values.members)]
      : [];

    // Upload handbook file if selected
    let handbookUrl = values.handbook?.trim() || null;
    if (handbookFile) {
      try {
        const uploadResult = await uploadFileMutation.mutateAsync(handbookFile);
        handbookUrl = uploadResult.data.url;
      } catch (err: any) {
        showNotification({
          title: "Handbook upload failed",
          message:
            err?.response?.data?.message || "Failed to upload handbook file",
          color: "red",
        });
        return;
      }
    }

    // Upload logo file if selected
    let logoUrl = null;
    if (logoFile) {
      try {
        const uploadResult = await uploadFileMutation.mutateAsync(logoFile);
        logoUrl = uploadResult.data.url;
      } catch (err: any) {
        showNotification({
          title: "Logo upload failed",
          message:
            err?.response?.data?.message || "Failed to upload department logo",
          color: "red",
        });
        return;
      }
    }

    const payload: any = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      handbook: handbookUrl || null,
      status: values.status,
      organization: values.organization,
      members: normalizedMembers,

      /** ⭐ UPDATED — SEND LOGO URL INSTEAD OF BASE64 */
      departmentLogo: logoUrl ?? null,
      director: values.director ?? null,
    };

    // Only add director if it has a valid value
    if (
      values.supervisor &&
      values.supervisor !== "undefined" &&
      values.supervisor.trim() !== ""
    ) {
      payload.director = values.supervisor;
    }

    // Build working hours array (new format)
    const workingHours: any[] = [];

    // Add default working hours
    if (values.startTime && values.endTime) {
      let totalHours = values.totalHours;
      if (
        totalHours === undefined ||
        totalHours === null ||
        Number.isNaN(Number(totalHours))
      ) {
        const computed = computeTotalHours(values.startTime, values.endTime);
        if (computed !== undefined) totalHours = computed;
      }

      if (totalHours !== undefined) {
        workingHours.push({
          type: "default" as const,
          startTime: values.startTime,
          endTime: values.endTime,
          totalHours: Number(totalHours),
        });
      }
    }

    // Add all recurring working hours if enabled
    if (values.enableRecurringConfig && values.recurringConfigs.length > 0) {
      values.recurringConfigs.forEach((recurringConfig) => {
        const entry: any = {
          type: "recurring" as const,
          recurringenabled: true,
          recurrenceType: recurringConfig.recurrenceType,
          interval: recurringConfig.interval,
          date: recurringConfig.selectedDate,
          startTime: recurringConfig.startTime,
          endTime: recurringConfig.endTime,
          totalHours: recurringConfig.totalHours,
        };

        if (recurringConfig.recurrenceType !== "day") {
          entry.selectedDays = recurringConfig.selectedDays;
        }

        if (recurringConfig.recurrenceType === "monthly") {
          entry.monthlyPattern = "first";
        }

        workingHours.push(entry);
      });
    }

    if (workingHours.length > 0) {
      payload.workingHours = workingHours;
    }

    // Build overtime config (COMPLETE)
    if (typeof values.overtimeAllowed === "boolean") {
      payload.overtimeConfig = {
        allowed: values.overtimeAllowed,
        maxHoursPerDay:
          values.maxHoursPerDay !== undefined && values.maxHoursPerDay !== null
            ? Number(values.maxHoursPerDay)
            : undefined,
        maxHoursPerMonth:
          values.maxHoursPerMonth !== undefined &&
          values.maxHoursPerMonth !== null
            ? Number(values.maxHoursPerMonth)
            : undefined,
        overtimeRate:
          values.overtimeRate !== undefined && values.overtimeRate !== null
            ? Number(values.overtimeRate)
            : undefined,
        requiresApproval: values.overtimeRequiresApproval || false,
      };
    }

    // Build late arrival config (COMPLETE)
    if (values.graceMinutes !== undefined && values.graceMinutes !== null) {
      payload.lateArrivalConfig = {
        graceMinutes: Number(values.graceMinutes),
        markAsHalfDayAfter:
          values.lateMarkHalfDayAfterMinutes !== undefined &&
          values.lateMarkHalfDayAfterMinutes !== null
            ? Number(values.lateMarkHalfDayAfterMinutes)
            : undefined,
        deductionEnabled: values.lateDeductionEnabled || false,
        deductMinutesFor:
          values.lateDeductMinutesFor !== undefined &&
          values.lateDeductMinutesFor !== null
            ? Number(values.lateDeductMinutesFor)
            : undefined,
        forEveryMinutes:
          values.lateForEveryMinutes !== undefined &&
          values.lateForEveryMinutes !== null
            ? Number(values.lateForEveryMinutes)
            : undefined,
      };
    }

    // Build early departure config (NEW)
    if (
      values.earlyGraceMinutes !== undefined &&
      values.earlyGraceMinutes !== null
    ) {
      payload.earlyDepartureConfig = {
        graceMinutes: Number(values.earlyGraceMinutes),
        markHalfDayBeforeMinutes:
          values.earlyMarkHalfDayBeforeMinutes !== undefined &&
          values.earlyMarkHalfDayBeforeMinutes !== null
            ? Number(values.earlyMarkHalfDayBeforeMinutes)
            : undefined,
      };
    }

    // Build break config (new format) - support both duration and timeRange
    if (values.breakTimeEnabled) {
      if (
        values.breakType === "duration" &&
        values.breakDurationHours !== undefined &&
        values.breakDurationHours !== null
      ) {
        payload.break = {
          enabled: true,
          type: "duration",
          durationHours: Number(values.breakDurationHours),
        };
      } else if (
        values.breakType === "timeRange" &&
        values.breakStart &&
        values.breakEnd
      ) {
        payload.break = {
          enabled: true,
          type: "timeRange",
          startTime: values.breakStart,
          endTime: values.breakEnd,
        };
      }
    } else {
      payload.break = undefined;
    }

    // Build location config (NEW)
    if (values.isLocationCheck) {
      payload.isLocationCheck = true;
      if (values.lng && values.lat) {
        payload.location = {
          lng: values.lng,
          lat: values.lat,
        };
      }
    } else {
      payload.isLocationCheck = false;
    }

    // Timezone
    if (values.timezone) {
      payload.timezone = values.timezone;
    }

    createDepartmentMutation.mutate(payload, {
      onSuccess: (data) => {
        showNotification({
          title: "Success",
          message: data?.message ?? "Department created",
          color: "green",
        });
        form.reset();
        setHandbookFile(null);
        setHandbookPreview("");
        setLogoFile(null);
        setLogoPreview("");
        onClose();
      },
      onError: (err: any) => {
        const message =
          (err?.response?.data as { message?: string })?.message ||
          err?.message ||
          "Failed to create department";
        showNotification({
          title: "Error",
          message,
          color: "red",
        });
      },
    });
  };

  if (orgsLoading) {
    return (
      <Center style={{ padding: 20 }}>
        <Loader />
      </Center>
    );
  }

  const isUsingOrg = !!form.values.sameAsOrg;

  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc: any, key: string) => {
      return acc && acc[key] !== undefined ? acc[key] : undefined;
    }, obj);
  };

  const hasPermission = (permissionKey: string) => {
    const value = getNested(permissions, permissionKey);

    return (
      value === true ||
      (typeof value === "object" &&
        value !== null &&
        Object.values(value).some(Boolean))
    );
  };

  const WORKINGHOURS_PERMISSION = "configuration_management.department_configrations_hours";

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label="Department Name"
        mb={15}
        withAsterisk
        {...form.getInputProps("name")}
      />

      <Select
        label="Timezone"
        description="Attendance start/end times are interpreted in this timezone"
        placeholder="Select timezone"
        data={CANADA_TIMEZONES}
        searchable
        mb={15}
        {...form.getInputProps("timezone")}
      />

      <Textarea
        label="Description"
        mb={15}
        minRows={3}
        {...form.getInputProps("description")}
      />

      <div style={{ marginBottom: 15 }}>
        <Text size="sm" weight={500} mb={5}>
          Handbook (PDF/Document - Max 5MB)
        </Text>

        <input
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) {
              setHandbookFile(null);
              setHandbookPreview("");
              return;
            }

            // Validate file size (5MB = 5 * 1024 * 1024 bytes)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
              showNotification({
                title: "File too large",
                message: "Handbook file must be less than 5MB",
                color: "red",
              });
              e.target.value = "";
              return;
            }

            setHandbookFile(file);
            setHandbookPreview(file.name);
          }}
        />

        {handbookPreview && (
          <Text size="sm" color="blue" mt={5}>
            Selected: {handbookPreview}
          </Text>
        )}

        {form.values.handbook && !handbookFile && (
          <div style={{ marginTop: 8 }}>
            <Text size="sm" color="dimmed">
              Current:{" "}
              <Text
                component="a"
                href={form.values.handbook}
                target="_blank"
                rel="noopener noreferrer"
                color="blue"
              >
                View Handbook
              </Text>
            </Text>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              mt={4}
              onClick={() => {
                form.setFieldValue("handbook", "");
              }}
            >
              Remove Handbook
            </Button>
          </div>
        )}
      </div>

      <Select
        label="Status"
        data={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
        withAsterisk
        mb={15}
        {...form.getInputProps("status")}
      />

      <Select
        label="Organization"
        data={orgListFiltered}
        withAsterisk
        mb={15}
        value={form.values.organization}
        onChange={handleOrgSelectChange}
      />
      <Select
        label="Director"
        placeholder="Select director"
        data={directorOptions}
        mb={15}
        clearable
        searchable
        value={form.values.director}
        onChange={(val) => form.setFieldValue("director", val)}
        disabled={directorsLoading}
      />
      {/* <Select
        label="Supervisor"
        data={localUserOptionsw}
        mb={15}
        placeholder="Select a supervisor"
        value={form.values.supervisor}
        onChange={(val) => form.setFieldValue("supervisor", val)}
      /> */}

      <MultiSelect
        key={form.values.organization}
        label="Members"
        data={localUserOptions}
        searchable
        clearable
        placeholder="Select members"
        mb={15}
        value={form.values.members}
        onChange={handleMembersChange}
      />

      {/* ⭐ NEW LOGO UPLOAD FIELD */}
      <div style={{ marginBottom: 20 }}>
        <Text size="sm" weight={700} mb={5}>
          Department Logo
        </Text>

        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            // Store the actual file for upload
            setLogoFile(file);

            // Create preview for display
            const reader = new FileReader();
            reader.onloadend = () => {
              setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
          }}
        />

        {logoPreview && (
          <img
            src={logoPreview}
            alt="Department Logo Preview"
            style={{
              width: 90,
              height: 90,
              marginTop: 10,
              borderRadius: 8,
              border: "1px solid #ddd",
              padding: 5,
              objectFit: "contain",
            }}
          />
        )}
      </div>
      {hasPermission(WORKINGHOURS_PERMISSION) && (
        <div
          style={{ borderTop: "1px solid #eee", paddingTop: 12, marginTop: 12 }}
        >
          <Checkbox
            label="Same as Organization Configuration"
            mb={12}
            checked={form.values.sameAsOrg}
            onChange={(e) =>
              form.setFieldValue("sameAsOrg", e.currentTarget.checked)
            }
          />

          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 8,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            <TextInput
              label="Start Time"
              placeholder="08:00"
              type="time"
              sx={{ flex: 1, minWidth: 160 }}
              {...form.getInputProps("startTime")}
              disabled={isUsingOrg}
            />

            <TextInput
              label="End Time"
              placeholder="16:00"
              type="time"
              sx={{ flex: 1, minWidth: 160 }}
              {...form.getInputProps("endTime")}
              disabled={isUsingOrg}
            />

            <NumberInput
              label="Total Hours"
              sx={{ width: 140 }}
              value={form.values.totalHours ?? undefined}
              precision={2}
              step={0.01}
              min={0}
              onChange={(val) =>
                form.setFieldValue("totalHours", val ?? undefined)
              }
              disabled={isUsingOrg}
              readOnly
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <Text size="md" weight={600} mb="md">
              Break Time Configuration
            </Text>

            <Switch
              label="Break Time Enabled"
              {...form.getInputProps("breakTimeEnabled", { type: "checkbox" })}
              disabled={isUsingOrg}
              mb="md"
            />

            <Group>
              <Select
                label="Break Type"
                data={[
                  { value: "duration", label: "Duration" },
                  { value: "timeRange", label: "Time Range" },
                ]}
                {...form.getInputProps("breakType")}
                disabled={!form.values.breakTimeEnabled || isUsingOrg}
                sx={{ minWidth: 200 }}
              />
              {form.values.breakType === "duration" ? (
                <NumberInput
                  label="Break Duration (hours)"
                  value={form.values.breakDurationHours ?? undefined}
                  min={0}
                  precision={2}
                  step={0.25}
                  onChange={(val) =>
                    form.setFieldValue("breakDurationHours", val ?? undefined)
                  }
                  disabled={!form.values.breakTimeEnabled || isUsingOrg}
                />
              ) : (
                <Group grow>
                  <TextInput
                    label="Break Start Time"
                    type="time"
                    {...form.getInputProps("breakStart")}
                    disabled={!form.values.breakTimeEnabled || isUsingOrg}
                  />
                  <TextInput
                    label="Break End Time"
                    type="time"
                    {...form.getInputProps("breakEnd")}
                    disabled={!form.values.breakTimeEnabled || isUsingOrg}
                  />
                </Group>
              )}
            </Group>
          </div>

          {/* Recurring Schedule Configuration */}
          <Checkbox
            label="Recurring time configuration (optional)"
            mt="md"
            disabled={isUsingOrg}
            checked={form.values.enableRecurringConfig || false}
            onChange={(e) =>
              form.setFieldValue(
                "enableRecurringConfig",
                e.currentTarget.checked
              )
            }
          />

          {form.values.enableRecurringConfig && !isUsingOrg && (
            <>
              {form.values.recurringConfigs.map((config, index) => (
                <Paper key={config.id} p="md" withBorder mt="md">
                  <Group position="apart" mb="md">
                    <Text weight={500}>Recurring Configuration {index + 1}</Text>
                    {form.values.recurringConfigs.length > 1 && (
                      <ActionIcon
                        color="red"
                        variant="light"
                        onClick={() => removeRecurringConfig(index)}
                        title="Remove recurring configuration"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>

                  <Group grow>
                    <Select
                      label="Recurrence Type"
                      data={[
                        { value: "day", label: "Date" },
                        { value: "weekly", label: "Week" },
                        { value: "biweekly", label: "Bi-Week" },
                      ]}
                      {...form.getInputProps(`recurringConfigs.${index}.recurrenceType`)}
                    />
                    <NumberInput
                      label="Interval"
                      min={1}
                      {...form.getInputProps(`recurringConfigs.${index}.interval`)}
                    />
                    <TextInput
                      label="Select Date"
                      type="date"
                      required
                      {...form.getInputProps(`recurringConfigs.${index}.selectedDate`)}
                    />
                  </Group>

                  {form.values.recurringConfigs[index].recurrenceType !== "day" && (
                    <Group grow mt="md">
                      <MultiSelect
                        label="Selected Days"
                        data={[
                          { value: "monday", label: "Monday" },
                          { value: "tuesday", label: "Tuesday" },
                          { value: "wednesday", label: "Wednesday" },
                          { value: "thursday", label: "Thursday" },
                          { value: "friday", label: "Friday" },
                          { value: "saturday", label: "Saturday" },
                          { value: "sunday", label: "Sunday" },
                        ]}
                        placeholder="Select days"
                        required
                        {...form.getInputProps(`recurringConfigs.${index}.selectedDays`)}
                      />
                    </Group>
                  )}

                  <Group grow mt="md">
                    <TextInput
                      label="Start Time"
                      type="time"
                      {...form.getInputProps(`recurringConfigs.${index}.startTime`)}
                    />
                    <TextInput
                      label="End Time"
                      type="time"
                      {...form.getInputProps(`recurringConfigs.${index}.endTime`)}
                    />
                    <NumberInput
                      label="Total Hours"
                      readOnly
                      step={0.25}
                      precision={2}
                      {...form.getInputProps(`recurringConfigs.${index}.totalHours`)}
                    />
                  </Group>
                </Paper>
              ))}

              <Button
                variant="outline"
                leftIcon={<IconPlus size={16} />}
                onClick={addRecurringConfig}
                type="button"
                mt="md"
              >
                Add Recurring Configuration
              </Button>
            </>
          )}

          {/* Overtime Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Overtime Configuration
            </Text>

            <Switch
              label="Overtime Allowed"
              checked={!!form.values.overtimeAllowed}
              onChange={(event) =>
                form.setFieldValue(
                  "overtimeAllowed",
                  event.currentTarget.checked
                )
              }
              disabled={isUsingOrg}
              mb="md"
            />

            <Group align="flex-end">
              <NumberInput
                label="Max Hours Per Day"
                value={form.values.maxHoursPerDay ?? undefined}
                precision={2}
                step={0.25}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("maxHoursPerDay", val ?? undefined)
                }
                disabled={isUsingOrg || !form.values.overtimeAllowed}
              />

              <NumberInput
                label="Max Hours Per Month"
                value={form.values.maxHoursPerMonth ?? undefined}
                precision={1}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("maxHoursPerMonth", val ?? undefined)
                }
                disabled={isUsingOrg || !form.values.overtimeAllowed}
              />

              <NumberInput
                label="Overtime Rate"
                value={form.values.overtimeRate ?? undefined}
                precision={1}
                step={0.1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("overtimeRate", val ?? undefined)
                }
                disabled={isUsingOrg || !form.values.overtimeAllowed}
              />

              <Switch
                label="Requires Approval"
                checked={!!form.values.overtimeRequiresApproval}
                onChange={(event) =>
                  form.setFieldValue(
                    "overtimeRequiresApproval",
                    event.currentTarget.checked
                  )
                }
                disabled={isUsingOrg || !form.values.overtimeAllowed}
              />
            </Group>
          </div>

          {/* Late Arrival Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Late Arrival Configuration
            </Text>

            <Group>
              <NumberInput
                label="Grace Minutes"
                value={form.values.graceMinutes ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("graceMinutes", val ?? undefined)
                }
                disabled={isUsingOrg}
              />

              <NumberInput
                label="Mark as Half Day After (minutes)"
                value={form.values.lateMarkHalfDayAfterMinutes ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue(
                    "lateMarkHalfDayAfterMinutes",
                    val ?? undefined
                  )
                }
                disabled={isUsingOrg}
              />

              <Switch
                label="Deduction Enabled"
                checked={!!form.values.lateDeductionEnabled}
                onChange={(event) =>
                  form.setFieldValue(
                    "lateDeductionEnabled",
                    event.currentTarget.checked
                  )
                }
                disabled={isUsingOrg}
              />

              <NumberInput
                label="Deduct Minutes For"
                value={form.values.lateDeductMinutesFor ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("lateDeductMinutesFor", val ?? undefined)
                }
                disabled={isUsingOrg || !form.values.lateDeductionEnabled}
              />

              <NumberInput
                label="For Every Minutes"
                value={form.values.lateForEveryMinutes ?? undefined}
                precision={0}
                step={1}
                min={1}
                onChange={(val) =>
                  form.setFieldValue("lateForEveryMinutes", val ?? undefined)
                }
                disabled={isUsingOrg || !form.values.lateDeductionEnabled}
              />
            </Group>
          </div>

          {/* Early Departure Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Early Departure Configuration
            </Text>

            <Group>
              <NumberInput
                label="Grace Minutes"
                value={form.values.earlyGraceMinutes ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("earlyGraceMinutes", val ?? undefined)
                }
                disabled={isUsingOrg}
              />

              <NumberInput
                label="Mark as Half Day Before (minutes)"
                value={form.values.earlyMarkHalfDayBeforeMinutes ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue(
                    "earlyMarkHalfDayBeforeMinutes",
                    val ?? undefined
                  )
                }
                disabled={isUsingOrg}
              />
            </Group>
          </div>

          {/* Location Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Location
            </Text>
            <Checkbox
              label="Enable Location Check"
              checked={form.values.isLocationCheck || false}
              onChange={(e) =>
                form.setFieldValue("isLocationCheck", e.currentTarget.checked)
              }
              disabled={isUsingOrg}
              mb="md"
            />

            {form.values.isLocationCheck && (
              <Group grow>
                <TextInput
                  label="Longitude"
                  placeholder="Enter longitude"
                  value={form.values.lng ?? ""}
                  onChange={(e) =>
                    form.setFieldValue("lng", e.currentTarget.value)
                  }
                  disabled={isUsingOrg}
                />
                <TextInput
                  label="Latitude"
                  placeholder="Enter latitude"
                  value={form.values.lat ?? ""}
                  onChange={(e) =>
                    form.setFieldValue("lat", e.currentTarget.value)
                  }
                  disabled={isUsingOrg}
                />
              </Group>
            )}
          </div>
        </div>
      )}

      <Group position="right" spacing={20} mt={20}>
        <Button color="cancel" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="filled"
          disabled={!form.isDirty()}
          loading={
            createDepartmentMutation.isLoading || uploadFileMutation.isLoading
          }
        >
          Add Department
        </Button>
      </Group>
    </form>
  );
}
